import React, { useRef } from 'react';
import { usePrintHook } from '../../hooks/usePrintHook';
import { codySalesJuneData } from '../../data/codySalesJuneData';
import CCMarketingReport from './CCMarketingReport';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const StandaloneCCJuneReport: React.FC = () => {
  const reportRef = useRef<HTMLDivElement>(null);
  const { printRef } = usePrintHook();
  
  // Generate PDF from report
  const generatePDF = async () => {
    if (!reportRef.current) return;
    
    try {
      // Create filename for the PDF
      const fileName = `ROI_CC_Marketing_Report_June_2025.pdf`;
      
      // Get the elements
      const reportElement = reportRef.current;
      
      // Temporarily hide print buttons (to prevent them from appearing in the PDF)
      const printButtons = reportElement.querySelectorAll('.print-button');
      printButtons.forEach(btn => {
        if (btn instanceof HTMLElement) {
          btn.style.display = 'none';
        }
      });
      
      // Capture the report content
      const canvas = await html2canvas(reportElement, {
        scale: 1.5, // Higher scale for better quality
        useCORS: true, // Allow images from other domains
        allowTaint: true, // Allow tainted canvas
        logging: false,
      });
      
      // Restore print buttons
      printButtons.forEach(btn => {
        if (btn instanceof HTMLElement) {
          btn.style.display = '';
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Page setup (portrait A4)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Load logo directly into PDF (separated from main content)
      const logoUrl = "https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png";
      
      // Create a temporary Image object to get logo dimensions
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = logoUrl;
      
      await new Promise<void>((resolve) => {
        logoImg.onload = () => resolve();
        if (logoImg.complete) resolve();
        setTimeout(resolve, 2000); // Failsafe timeout
      });
      
      // Add logo to first page
      const logoWidth = 90; // mm - logo width in PDF
      const logoX = (pdfWidth - logoWidth) / 2; // Center logo
      const logoY = 10; // Top margin
      
      try {
        const logoCanvas = document.createElement('canvas');
        logoCanvas.width = logoImg.width;
        logoCanvas.height = logoImg.height;
        const ctx = logoCanvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(logoImg, 0, 0, logoImg.width, logoImg.height);
          const logoDataUrl = logoCanvas.toDataURL('image/png');
          const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
          pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
        }
      } catch (logoError) {
        console.error('Error adding logo:', logoError);
        // Continue without logo if there's an error
      }
      
      // Add title text
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.setTextColor(50, 50, 50);
      pdf.text('ROI Exteriors - Cody Cline Performance Report', pdfWidth/2, 50, { align: 'center' });
      pdf.setFontSize(14);
      pdf.text('June 2025', pdfWidth/2, 60, { align: 'center' });
      
      // Calculate ratio to fit content to PDF
      const canvasRatio = canvas.height / canvas.width;
      const contentWidth = pdfWidth;
      const contentHeight = contentWidth * canvasRatio;
      
      // Add main content with offset to accommodate the logo and title
      const contentY = 70; // Start below the logo and title
      
      // Add content spread across multiple pages
      let heightLeft = contentHeight;
      let position = contentY - contentY; // Adjust initial position to show from the top
      let page = 1;
      
      pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
      heightLeft -= (pdfHeight - contentY);
      
      // Add more pages if content overflows
      while (heightLeft > 0) {
        pdf.addPage();
        page++;
        position = -(pdfHeight - contentY) * (page - 1) + contentY;
        pdf.addImage(imgData, 'PNG', 0, position, contentWidth, contentHeight);
        heightLeft -= pdfHeight;
      }
      
      // Add footer on each page
      for (let i = 0; i < page; i++) {
        pdf.setPage(i + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`ROI Exteriors Marketing Report - Page ${i + 1} of ${page}`, 10, pdfHeight - 5);
      }
      
      // Download PDF
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };
  
  // Handle export to PDF
  const handleExportPDF = () => {
    generatePDF();
  };
  
  // Handle print
  const handlePrint = () => {
    if (reportRef.current) {
      // Cast the ref to the expected type
      printRef(reportRef as unknown as React.RefObject<HTMLElement>);
    }
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="mb-4 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">
            Cody Cline - June 2025 Report
          </h2>
          <div className="flex space-x-3">
            <button
              onClick={handlePrint}
              className="print-button px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
              </svg>
              Print
            </button>
            <button
              onClick={handleExportPDF}
              className="print-button px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
              Export PDF
            </button>
            
          </div>

        </div>
        
        <div className="border p-4 rounded shadow-sm mb-4 bg-green-50">
          <h3 className="text-lg font-semibold mb-2">Cody Cline June 2025 Report</h3>
          <p className="mb-3">
            This report presents June 2025 performance data for Cody Cline&apos;s team using the specialized 
            compensation structure where net profit is calculated as Gross Profit minus Company Overhead, 
            with sales commission being 50% of the net profit.
          </p>
          <p className="mb-2">
            <strong>Period:</strong> June 1-30, 2025<br />
            <strong>Total Jobs:</strong> {codySalesJuneData.jobData.length}<br />
            <strong>Total Sales:</strong> ${codySalesJuneData.kpis.totalSales.toLocaleString()}<br />
            <strong>Net Profit:</strong> ${codySalesJuneData.kpis.netProfit.toLocaleString()}<br />
            <strong>Expected Commission Payout:</strong> ${codySalesJuneData.kpis.salesCommissions.toLocaleString()}<br />
          </p>
        </div>
        
        {/* Wrap report in a div with ref for PDF export */}
        <div ref={reportRef}>
          {/* Header with logo */}
          <div className="text-center mb-8">
            <img
              src="https://roiconstruction.com/wp-content/uploads/2024/04/Marylands-Premier-Roofer-ROI-Construction-Roofing-Siding-Windows-Doors.png"
              alt="ROI Construction Logo"
              className="mx-auto h-20 mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-800">ROI Exteriors - Cody Cline Performance Report</h1>
            <h2 className="text-xl text-gray-600">June 2025</h2>
          </div>
          
          <CCMarketingReport data={codySalesJuneData} hideHeader={false} />
        </div>
      </div>
    </div>
  );
};

export default StandaloneCCJuneReport;